// src/layouts/DashboardLayout.js
import React, { useState, useContext, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Video, 
  User, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  ChevronRight,
  Rocket
} from 'lucide-react';
import { signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { toast } from 'react-toastify';
import HelpButton from '../components/HelpButton';

function DashboardLayout() {
  const { currentUser, userRole, hasValidSubscription, subscriptionTier } = useContext(AuthContext);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const truncatedEmail = currentUser?.email && currentUser.email.length > 18 
    ? `${currentUser.email.substring(0, 15)}...` 
    : currentUser?.email;
    
  // Fetch user data from Firestore to get the full name
  useEffect(() => {
    if (currentUser?.uid) {
      const fetchUserData = async () => {
        try {
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userSnapshot = await getDoc(userDocRef);
          if (userSnapshot.exists()) {
            setUserData(userSnapshot.data());
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      };
      fetchUserData();
    }
  }, [currentUser]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      toast.success('Successfully logged out');
    } catch (error) {
      toast.error('Failed to log out');
    }
  };
  
  const handleOpenHelp = () => {
    // Navigate to dashboard where the checklist is located
    navigate('/dashboard/home');
  };

  // Navigation links with active state
  const navigation = [
    { name: 'Dashboard', href: '/dashboard/home', icon: LayoutDashboard, current: location.pathname.includes('/dashboard') },
    { name: 'Record Video', href: '/record', icon: Video, current: location.pathname === '/record' },
    { name: 'Profile', href: '/profile', icon: User, current: location.pathname === '/profile' },
  ];

  // Add admin link if user is admin
  if (userRole === 'admin') {
    navigation.push({ name: 'Admin', href: '/admin/home', icon: Settings, current: location.pathname.includes('/admin') });
  }

  // Determine if sidebar should be shown - hide for users without subscription
  // Except don't hide on profile page (which is needed to purchase subscription)
  const shouldHideSidebar = !hasValidSubscription && location.pathname !== '/profile';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Mobile sidebar - conditionally rendered */}
      {!shouldHideSidebar && (
        <div className="lg:hidden">
          <div className="fixed inset-0 bg-gray-800 bg-opacity-75 z-40 transition-opacity duration-300"
               style={{ display: sidebarOpen ? 'block' : 'none' }}
               onClick={() => setSidebarOpen(false)}></div>
          
          <div className={`fixed inset-y-0 left-0 flex flex-col w-64 bg-white shadow-lg z-50 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out`}>
          <div className="flex items-center justify-between px-4 py-5 border-b">
            <span className="text-xl font-semibold">ReelIntro</span>
            <button onClick={() => setSidebarOpen(false)} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto py-4">
            <div className="px-2 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`${
                    item.current
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } group flex items-center px-2 py-2 text-base font-medium rounded-md`}
                >
                  <item.icon className={`${
                    item.current ? 'text-gray-800' : 'text-gray-400 group-hover:text-gray-500'
                  } mr-3 h-6 w-6`} />
                  {item.name}
                </Link>
              ))}
            </div>
            {!hasValidSubscription && (
              <div className="mt-10 px-3">
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <div className="text-sm font-semibold text-yellow-800">No active plan</div>
                  <div className="text-xs text-yellow-700 mt-1">
                    You need a subscription to access all features
                  </div>
                  <Link to="/profile" className="mt-3 text-sm text-yellow-800 bg-yellow-200 px-2 py-1 rounded flex items-center justify-center">
                    Get a plan <ChevronRight className="w-4 h-4 ml-1" />
                  </Link>
                </div>
              </div>
            )}
            <div className="mt-auto px-3 py-4 border-t">
              <div className="flex flex-col space-y-3">
                <button
                  onClick={handleOpenHelp}
                  className="flex items-center w-full px-2 py-2 text-base font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900"
                >
                  <Rocket className="mr-3 h-6 w-6 text-brand-600" />
                  Launchpad
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-2 py-2 text-base font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900"
                >
                  <LogOut className="mr-3 h-6 w-6 text-gray-400" />
                  Sign out
                </button>
              </div>
            </div>
          </nav>
        </div>
      </div>
      )}

      {/* Desktop sidebar - conditionally rendered */}
      {!shouldHideSidebar && (
        <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
          <div className="flex flex-col flex-1 h-full border-r border-gray-200 bg-white">
            <div className="flex items-center h-16 flex-shrink-0 px-4 border-b border-gray-200">
              <Link to="/" className="text-xl font-semibold text-gray-900">
                ReelIntro
              </Link>
            </div>
            <div className="flex-1 flex flex-col pt-5 overflow-y-auto">
              <nav className="flex-1 px-2 space-y-1 bg-white">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`${
                      item.current
                        ? 'bg-gray-100 text-gray-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    } group flex items-center px-2 py-2 text-sm font-medium rounded-md`}
                  >
                    <item.icon className={`${
                      item.current ? 'text-gray-800' : 'text-gray-400 group-hover:text-gray-500'
                    } mr-3 h-5 w-5`} />
                    {item.name}
                  </Link>
                ))}
              </nav>
              {!hasValidSubscription && (
                <div className="px-3 mt-6">
                  <div className="p-3 bg-yellow-50 rounded-lg">
                    <div className="text-sm font-semibold text-yellow-800">No active plan</div>
                    <div className="text-xs text-yellow-700 mt-1">
                      You need a subscription to use all features
                    </div>
                    <Link to="/profile" className="mt-3 text-xs text-yellow-800 bg-yellow-200 px-2 py-1 rounded flex items-center justify-center">
                      Get a plan <ChevronRight className="w-3 h-3 ml-1" />
                    </Link>
                  </div>
                </div>
              )}
            </div>
            <div className="flex-shrink-0 border-t border-gray-200 p-4">
              <div className="flex flex-col space-y-4">
                <button
                  onClick={handleOpenHelp}
                  className="flex items-center justify-center p-2 bg-brand-50 text-brand-600 rounded-lg hover:bg-brand-100 transition-colors"
                >
                  <Rocket className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">Launchpad</span>
                </button>
                
                <button 
                  onClick={handleLogout}
                  className="flex-shrink-0 w-full group block"
                >
                  <div className="flex items-center">
                    <div>
                      <User className="inline-block h-9 w-9 rounded-full text-gray-400 bg-gray-100 p-2" />
                    </div>
                    <div className="ml-3 overflow-hidden">
                      <p className="text-sm font-medium text-gray-700 group-hover:text-gray-900 truncate">
                        {userData?.fullName || truncatedEmail || 'User'}
                      </p>
                      <p className="text-xs font-medium text-gray-500 group-hover:text-gray-700">
                        Sign out
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={`${!shouldHideSidebar ? 'lg:pl-64' : ''} flex flex-col flex-1`}>
        {/* Mobile header */}
        <div className="sticky top-0 z-10 flex-shrink-0 flex h-16 bg-white border-b border-gray-200 lg:hidden">
          {!shouldHideSidebar && (
            <button
              onClick={() => setSidebarOpen(true)}
              className="px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 lg:hidden"
            >
              <span className="sr-only">Open sidebar</span>
              <Menu className="h-6 w-6" />
            </button>
          )}
          <div className="flex-1 flex justify-between px-4">
            <div className="flex-1 flex items-center">
              <Link to="/" className="text-xl font-semibold text-gray-900">
                ReelIntro
              </Link>
              
              {shouldHideSidebar && (
                <div className="ml-4 text-sm text-yellow-600 bg-yellow-100 px-3 py-1 rounded-full">
                  Subscription Required
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main content */}
        <main className="flex-1">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <Outlet />
            </div>
          </div>
        </main>
        
        {/* Footer - forced to bottom with sufficient padding to prevent overlap */}
        <footer className="bg-white border-t border-gray-200 py-6 mt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
            <div className="flex flex-col items-center justify-center">
              <div className="text-center">
                <p className="text-gray-500 text-sm">
                  &copy; {new Date().getFullYear()} ReelIntro. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default DashboardLayout;